import type { Page } from '@hello-aria/api-client'
import {
    useInfiniteQuery,
    UseInfiniteQueryResult,
    InfiniteData,
    useQuery,
    UseQueryResult,
    UseMutationResult,
    useQueryClient,
    useMutation
} from '@tanstack/react-query'

import { useMemo } from 'react'
import { membersTableFields } from '../../modules/settings/enums/membersEnums'
import { CreateMember, Member, memberPrivileges, MemberPrivileges, UpdateMember } from '../models/memberModel'
import { QueryParams as QueryParamsState } from '../models/queryModels'
import { createMember, deleteMember, getMember, listMembers, updateMember } from '../services/membersServices'
import { mapQuery } from '../utils/queryUtils'
import { useMe } from './useMe'
import { useQueryParamsState } from './useQueryParams'
import { deprecated_useSetToast } from './useSetToast'

const membersQueryKeys = {
    root: ['infiniteMembers'],
    infiniteMembers: (
        companyId: string,
        queryParamsState?: QueryParamsState
    ): (string | QueryParamsState | undefined)[] => [...membersQueryKeys.root, companyId, queryParamsState]
}

export const useInfiniteMembers = (): UseInfiniteQueryResult<InfiniteData<Page<Member>>> => {
    const { companyId } = useMe()
    const queryParamsState = useQueryParamsState()

    return useInfiniteQuery({
        queryKey: membersQueryKeys.infiniteMembers(
            companyId,
            queryParamsState.pick(['members_search', 'members_sort'])
        ),

        queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
            listMembers(
                companyId,
                mapQuery({
                    sortBy: queryParamsState.get('members_sort'),
                    pagination: { after: pageParam },
                    search: {
                        value: queryParamsState.get('members_search'),
                        path: membersTableFields
                    }
                })
            ),

        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },

        initialPageParam: undefined
    })
}

export const useMemberById = (): UseQueryResult<Member> => {
    const { companyId, isGod } = useMe()
    return useQuery({
        queryKey: ['member', companyId],
        queryFn: () => getMember(companyId, 'me'),
        enabled: !isGod,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnMount: false
    })
}

export const useUpdateMember = ({
    shouldTriggerToaster = true
}): UseMutationResult<Member, unknown, UpdateMember, unknown> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()
    const queryParamsState = useQueryParamsState()

    const setToast = deprecated_useSetToast()

    return useMutation({
        mutationFn: (payload: UpdateMember) => updateMember(companyId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: membersQueryKeys.infiniteMembers(
                    companyId,
                    queryParamsState.pick(['members_search', 'members_sort'])
                )
            })
            shouldTriggerToaster && setToast('updateMemberRole', 'positive')
        },
        onError: () => {
            shouldTriggerToaster && setToast('updateMemberRole', 'negative')
        }
    })
}

export const useDeleteMember = (): UseMutationResult<Member, unknown, string, unknown> => {
    const { companyId } = useMe()

    const mutation = useMutation({
        mutationFn: (id: string) => deleteMember(companyId, id)
    })

    return mutation
}

export const useInvalidateMembers = () => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    return () => {
        queryClient.invalidateQueries({
            queryKey: membersQueryKeys.infiniteMembers(companyId)
        })
    }
}

export const useCreateMember = ({
    shouldTriggerToaster = true
}): UseMutationResult<Member, unknown, CreateMember, unknown> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()
    const queryParamsState = useQueryParamsState()

    const setToast = deprecated_useSetToast()

    return useMutation({
        mutationFn: (member: CreateMember) => createMember(companyId, member),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: membersQueryKeys.infiniteMembers(
                    companyId,
                    queryParamsState.pick(['members_search', 'members_sort'])
                )
            })
            shouldTriggerToaster && setToast('postMember', 'positive')
        },
        onError: () => {
            shouldTriggerToaster && setToast('postMember', 'negative')
        }
    })
}

export const hasPrivilege = (
    userPrivilege: MemberPrivileges | undefined,
    requiredPrivilege: MemberPrivileges
): boolean => {
    if (!userPrivilege) return false

    const userIndex = memberPrivileges.indexOf(userPrivilege)
    const requiredIndex = memberPrivileges.indexOf(requiredPrivilege)

    return userIndex >= requiredIndex
}

type UsePrivilegesOptions = {
    member: Member | undefined
    requiredPrivilege?: MemberPrivileges
}

type UsePrivilegesReturn = {
    member: Member | undefined
    isAuthorized: boolean
}

export const usePrivileges = ({
    member,
    requiredPrivilege = 'readwrite'
}: UsePrivilegesOptions): UsePrivilegesReturn => {
    const hasRequiredPrivilege = useMemo(
        () => hasPrivilege(member?.privileges, requiredPrivilege),
        [member?.privileges, requiredPrivilege]
    )

    const isAuthorized = useMemo(() => Boolean(member && hasRequiredPrivilege), [member, hasRequiredPrivilege])

    return {
        isAuthorized,
        member
    }
}
